<template>
  <div>
    <navigation-top/>

    <div class="pt-8 content">
      <v-btn
        v-if="canClose"
        class="closeButton"
        icon="mdi-close"
        variant="text"
        color="grey"
        aria-label="Close"
        @click="close"
      />
      <div class="header">
        <h2 class="pageTitle">{{ pageTitle }}</h2>
        <p class="pageDescription">{{ pageDescription }}</p>
      </div>

      <billing-period-switch v-model="localSubscriptionType" class="mb-10" />

      <div class="plans">
        <plan-card
          :key="plan.value"
          v-for="plan in PLANS"
          :name="plan.name"
          :description="plan.description"
          :monthlyPrice="plan.monthlyPrice"
          :yearlyPrice="plan.yearlyPrice"
          :type="localSubscriptionType"
          :popular="plan.value === 'pro'"
          :disabled="loadingStartTrial"
          @onSelect="openCheckout(plan.value)"
        />
        <plan-card
          v-if="ENTERPRISE_PLAN"
          :name="ENTERPRISE_PLAN.name"
          :description="ENTERPRISE_PLAN.description"
          :disabled="loadingStartTrial"
          @onSelect="openContactUs"
        />
      </div>
      <p class="vatNote">Prices exclude VAT.</p>

      <div class="compareInfo">
        <p class="compareInfoTitle">See how {{ brand.name }} compares to other tools</p>

        <table class="compareInfoTable">
          <thead>
            <tr>
              <th class="compareInfoTableHeader"></th>
              <th class="compareInfoTableHeader">
                <brand-logo class="logo nav-logo" variant="mono" height="30" />
              </th>
              <th class="compareInfoTableHeader">Miro</th>
              <th class="compareInfoTableHeader">LucidChart</th>
              <th class="compareInfoTableHeader">Visio</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="info in compareInfo" :key="info.feature">
              <td class="compareInfoTableItem">
                <div class="compareInfoTableItemInner">
                  {{ info.feature }}
                </div>
              </td>
              <td class="compareInfoTableItem">
                <div class="compareInfoTableItemInner">
                  <template v-if="info.ours">
                    <img
                      class="logo"
                      alt="Included"
                      src="@/assets/compare-checked.svg"
                    />
                  </template>
                  <template v-else>
                    <img
                      class="logo"
                      alt="Not included"
                      src="@/assets/compare-unchecked.svg"
                    />
                  </template>
                </div>
              </td>
              <td class="compareInfoTableItem">
                <div class="compareInfoTableItemInner">
                  <template v-if="info.miro">
                    <img
                      class="logo"
                      alt="Included"
                      src="@/assets/compare-checked.svg"
                    />
                  </template>
                  <template v-else>
                    <img
                      class="logo"
                      alt="Not included"
                      src="@/assets/compare-unchecked.svg"
                    />
                  </template>
                </div>
              </td>
              <td class="compareInfoTableItem">
                <div class="compareInfoTableItemInner">
                  <template v-if="info.lucidChart">
                    <img
                      class="logo"
                      alt="Included"
                      src="@/assets/compare-checked.svg"
                    />
                  </template>
                  <template v-else>
                    <img
                      class="logo"
                      alt="Not included"
                      src="@/assets/compare-unchecked.svg"
                    />
                  </template>
                </div>
              </td>
              <td class="compareInfoTableItem">
                <div class="compareInfoTableItemInner">
                  <template v-if="info.visio">
                    <img
                      class="logo"
                      alt="Included"
                      src="@/assets/compare-checked.svg"
                    />
                  </template>
                  <template v-else>
                    <img
                      class="logo"
                      alt="Not included"
                      src="@/assets/compare-unchecked.svg"
                    />
                  </template>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="faq">
        <div class="faqInner">
          <v-expansion-panels>
            <v-expansion-panel
              collapse-icon="mdi-minus" expand-icon="mdi-plus"
              :title="`Can I try ${brand.name} for free?`"
              :text="`Yes, every new account starts with a free 2-week trial of ${brand.name} with all its features, and no credit card is required. Just sign up and start. Choose a plan only if you want to continue after the trial ends.`"
            >
            </v-expansion-panel>

            <v-expansion-panel
              collapse-icon="mdi-minus" expand-icon="mdi-plus"
              :title="`Who is ${brand.name} for?`"
              text="Anyone involved in transforming business needs to a business application, such as Product Owners, Product Managers, Architects, Business Analysts, CTOs, Founders, Team Leaders, Project Managers, Scrum Masters, etc."
            >
            </v-expansion-panel>

            <v-expansion-panel
              collapse-icon="mdi-minus" expand-icon="mdi-plus"
              title="Is my data secure?"
            >
              <template v-slot:text>
                We take security seriously and are committed to protecting your data. We comply with GDPR and follow
                strict security measures.
                <a v-if="brand.links.security" :href="brand.links.security" target="_blank">Learn more</a>.
              </template>
            </v-expansion-panel>

            <v-expansion-panel
              collapse-icon="mdi-minus" expand-icon="mdi-plus"
              title="What payment options do you accept?"
              text="We accept Visa, Mastercard, and other major credit cards. We also accept bank transfers and invoices."
            >
            </v-expansion-panel>

            <v-expansion-panel
              collapse-icon="mdi-minus" expand-icon="mdi-plus"
              title="What happens after my free trial ends?"
              text="Since no credit card is required to start, nothing is charged automatically. Before the 2-week trial ends, choose the plan that fits you to keep going. If you don't, your account simply switches to read-only - your projects and workflows stay safe, you can still export everything to Jira, Azure DevOps or as JSON, CSV or PDF, and you can choose a plan anytime."
            >
            </v-expansion-panel>

            <v-expansion-panel
              collapse-icon="mdi-minus" expand-icon="mdi-plus"
              title="What happens to my data if I cancel?"
              text="Your data will be stored for 6 months, allowing you to restore it if you decide to re-subscribe later."
            >
            </v-expansion-panel>

            <v-expansion-panel
              collapse-icon="mdi-minus" expand-icon="mdi-plus"
              title="Can I extend the trial period?"
            >
              <template v-slot:text>
                Yes, send an email to <a :href="`mailto:${brand.emails.support}`">{{ brand.emails.support }}</a> and we will extend
                your trial for 2 more weeks.
              </template>
            </v-expansion-panel>

            <v-expansion-panel
              collapse-icon="mdi-minus" expand-icon="mdi-plus"
              title="Do you offer discounts for annual plans?"
              text="Yes, we offer a 17% discount on annual plans."
            >
            </v-expansion-panel>

            <v-expansion-panel
              collapse-icon="mdi-minus" expand-icon="mdi-plus"
              title="Can I switch plans later?"
              text="Yes, you can switch from both Basic to Pro as well as from Pro to Basic."
            >
            </v-expansion-panel>

            <v-expansion-panel
              collapse-icon="mdi-minus" expand-icon="mdi-plus"
              title="Can I switch from monthly to yearly payment?"
              :text="`Yes, by going to “My Plan” in the ${brand.name} app you can update to annual billing.`"
            >
            </v-expansion-panel>

            <v-expansion-panel
              collapse-icon="mdi-minus" expand-icon="mdi-plus"
              title="Is it possible to add or remove users later in the plan?"
              :text="`Yes, you can add or remove user seats in both the ${brand.name} Pro and ${brand.name} Basic plans. When removing a seat, you can either lower the cost by removing the seat or assign it to a new user.`"
            >
            </v-expansion-panel>

            <v-expansion-panel
              collapse-icon="mdi-minus" expand-icon="mdi-plus"
              title="What happens if I cancel my subscription?"
              text="Your subscription will end automatically on the last day of your current subscription period."
            >
            </v-expansion-panel>

            <v-expansion-panel
              collapse-icon="mdi-minus" expand-icon="mdi-plus"
              title="Is there a limit to the number of AI prompts?"
              :text="`No, with both the ${brand.name} Pro and Basic plan, you have unlimited AI prompts.`"
            >
            </v-expansion-panel>

            <v-expansion-panel
              collapse-icon="mdi-minus" expand-icon="mdi-plus"
              :title="`Does ${brand.name} integrate with other products?`"
              :text="`Yes, ${brand.name} allows you to export cards to Jira and Azure DevOps. You can also export data as a CSV file, which can be opened in Google Sheets or Excel, or in JSON format. Custom integrations available in the Enterprise plan.`"
            >
            </v-expansion-panel>

            <v-expansion-panel
              collapse-icon="mdi-minus" expand-icon="mdi-plus"
              title="Is there a discount for startups, students, etc?"
            >
              <template v-slot:text>
                We offer discounts on a case-by-case basis. Please <a
                  target="_blank"
                  href="https://2gpm9s.share-eu1.hsforms.com/2Fdo_b7-hSES99T__J3YREA">contact
                  us</a>.
              </template>
            </v-expansion-panel>

            <v-expansion-panel
              collapse-icon="mdi-minus" expand-icon="mdi-plus"
              title="Are there any applicable taxes?"
            >
              <template v-slot:text>
                {{ brand.company.vatNotice }} Please <a
                  target="_blank"
                  href="https://2gpm9s.share-eu1.hsforms.com/2Fdo_b7-hSES99T__J3YREA">contact
                  us</a> for further information.
              </template>
            </v-expansion-panel>

            <v-expansion-panel
              collapse-icon="mdi-minus" expand-icon="mdi-plus"
              title="Where can I find my invoices?"
              text="You can find your invoices under “Plan & Billing” > “Billing” in the top right corner, under the profile icon."
            >
            </v-expansion-panel>

            <v-expansion-panel
              collapse-icon="mdi-minus" expand-icon="mdi-plus"
              title="Do you offer onboarding or training?"
            >
              <template v-slot:text>
                Yes, we offer onboarding and training. Please <a
                  target="_blank"
                  href="https://2gpm9s.share-eu1.hsforms.com/2Fdo_b7-hSES99T__J3YREA">contact
                  us</a> to schedule a training session
              </template>
            </v-expansion-panel>

            <v-expansion-panel
              collapse-icon="mdi-minus" expand-icon="mdi-plus"
              :title="`Do I need to install any software to use ${brand.name}?`"
              :text="`No, ${brand.name} is entirely web based and can be used with Chrome or Edge. The ${brand.name} app works on all computers and operating systems.`"
            >
            </v-expansion-panel>

            <v-expansion-panel
              collapse-icon="mdi-minus" expand-icon="mdi-plus"
              title="How can I suggest a new feature?"
            >
              <template v-slot:text>
                Please fill out our support form <a
                  target="_blank"
                  href="https://2gpm9s.share-eu1.hsforms.com/2Fdo_b7-hSES99T__J3YREA">here</a>.
              </template>
            </v-expansion-panel>

            <v-expansion-panel
              collapse-icon="mdi-minus" expand-icon="mdi-plus"
              title="How can I report a bug?"
            >
              <template v-slot:text>
                Please fill out our support form <a
                  target="_blank"
                  href="https://2gpm9s.share-eu1.hsforms.com/2Fdo_b7-hSES99T__J3YREA">here</a>.
              </template>
            </v-expansion-panel>

            <v-expansion-panel
              collapse-icon="mdi-minus" expand-icon="mdi-plus"
              title="How do I contact support?"
            >
              <template v-slot:text>
                Please fill out our support form <a
                  target="_blank"
                  href="https://2gpm9s.share-eu1.hsforms.com/2Fdo_b7-hSES99T__J3YREA">here</a>
                or
                <a href="tel:+46 (0)8 12420810">call us</a> at +46 (0)8 12420810.
              </template>
            </v-expansion-panel>

            <v-expansion-panel
              collapse-icon="mdi-minus" expand-icon="mdi-plus"
              title="How do I cancel my subscription?"
              :text="`It’s very easy to cancel your ${brand.name} subscription. You can find it under “Plan & Billing” > “My Plan” in the top right corner, under the profile icon.`"
            >
            </v-expansion-panel>
          </v-expansion-panels>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import store from '@/store'
import PlanCard from '@/components/PlanCard.vue'
import NavigationTop from '../components/NavigationTop.vue'
import { mapState } from 'vuex'
import { PLANS, ENTERPRISE_PLAN, SKIP_TRIAL_CHECKOUT, canStartCardlessTrial, getTrialDaysLeft, getTrialEndDate, hasEndedUnchosenTrial } from '@/utils/payments'
import BrandLogo from '@/components/BrandLogo.vue'
import BillingPeriodSwitch from '@/components/BillingPeriodSwitch.vue'
import brand from '@/composables/useBrand'
import { useStartTrial } from '@/composables/useStartTrial'

const PLAN_CHOICE_PATHS = ['/plans', '/checkout', '/payment']

const isAppPath = (path) => typeof path === 'string' && path.startsWith('/') && !path.startsWith('//')

const isPlanChoicePath = (path) => PLAN_CHOICE_PATHS.includes(path.split('?')[0])

export default {
  components: {
    BrandLogo,
    BillingPeriodSwitch,
    PlanCard,
    NavigationTop
  },
  setup () {
    const { startTrial, loadingStartTrial } = useStartTrial()

    return { startTrial, loadingStartTrial }
  },
  computed: {
    brand: () => brand,
    ...mapState({
      subscriptionType: state => state.payments.subscriptionType,
      activeSubscription: state => state.payments.activeSubscription,
      loadingActiveSubscription: state => state.payments.loadingActiveSubscription,
      isSubscriptionAdmin: state => state.payments.isSubscriptionAdmin,
      subscriptionAccessType: state => state.payments.subscriptionAccessType,
      currentUser: state => state.user.user
    }),
    trialEndDate () {
      return this.isSubscriptionAdmin ? getTrialEndDate(this.activeSubscription) : null
    },
    hasEndedTrial () {
      return this.isSubscriptionAdmin && hasEndedUnchosenTrial(this.activeSubscription)
    },
    pageTitle () {
      if (this.hasEndedTrial) {
        return 'Your free trial has ended'
      }

      if (!this.trialEndDate) {
        return `Try ${brand.name} for free`
      }

      const daysLeft = getTrialDaysLeft(this.activeSubscription)

      if (daysLeft === 0) {
        return 'Your free trial ends today'
      }

      return daysLeft === 1 ? 'Your free trial ends tomorrow' : `Your free trial ends in ${daysLeft} days`
    },
    pageDescription () {
      if (this.hasEndedTrial) {
        return 'Your projects and workflows are safe. Choose a plan to keep editing.'
      }

      return this.trialEndDate
        ? `Choose the plan that fits you to keep going. You won't be charged before ${this.trialEndDate}.`
        : 'Discover the potential of AI-Powered Software Modeling and Code Creation'
    },
    canClose () {
      return this.subscriptionAccessType !== 'NO_ACCESS'
    },
    PLANS () {
      return PLANS || []
    },
    ENTERPRISE_PLAN () {
      return ENTERPRISE_PLAN || null
    }
  },
  data () {
    return {
      localSubscriptionType: 'yearly',
      compareInfo: [
        { feature: 'Scrum Product Backlog', ours: true, miro: true, lucidChart: true, visio: false },
        { feature: 'Support for Event Storming', ours: true, miro: true, lucidChart: true, visio: false },
        { feature: 'Support for Domain-Driven Design', ours: true, miro: false, lucidChart: false, visio: false },
        {
          feature: 'Support for Given-When-Then (GWT) tests/requirements',
          ours: true,
          miro: false,
          lucidChart: false,
          visio: false
        },
        { feature: 'Support for User Story Mapping', ours: true, miro: true, lucidChart: true, visio: false },
        { feature: 'Support for Event Modeling', ours: true, miro: false, lucidChart: false, visio: false },
        { feature: 'Support for Data Modeling', ours: true, miro: false, lucidChart: false, visio: false },
        {
          feature: 'Automatic alignment and connection of diagram symbols',
          ours: true,
          miro: false,
          lucidChart: false,
          visio: false
        },
        {
          feature: 'AI generation of workflows, data models, API models, etc',
          ours: true,
          miro: false,
          lucidChart: false,
          visio: false
        },
        {
          feature: 'Code generation of frontend and backend code',
          ours: true,
          miro: false,
          lucidChart: false,
          visio: false
        },
        { feature: 'Code generation of unit tests', ours: true, miro: false, lucidChart: false, visio: false },
        { feature: 'Reverse engineering of legacy code', ours: true, miro: false, lucidChart: false, visio: false },
        {
          feature: 'Export of actionable software models (CSV, PDF, JSON, ...)',
          ours: true,
          miro: false,
          lucidChart: false,
          visio: false
        },
        { feature: 'Jira Cloud Integration', ours: true, miro: true, lucidChart: false, visio: false },
        { feature: 'Azure DevOps Integration', ours: true, miro: true, lucidChart: false, visio: false },
        { feature: 'Custom AI Model Integration', ours: true, miro: false, lucidChart: false, visio: false },
        { feature: 'Entra ID integration', ours: true, miro: false, lucidChart: false, visio: false },
        { feature: 'GitHub integration', ours: true, miro: false, lucidChart: false, visio: false },
        { feature: 'Custom integrations', ours: true, miro: false, lucidChart: false, visio: false },
        {
          feature: 'Automatic synchronisation between code and domain model',
          ours: true,
          miro: false,
          lucidChart: false,
          visio: false
        },
        {
          feature: 'Hybrid modeling: natural language + visual modeling',
          ours: true,
          miro: false,
          lucidChart: false,
          visio: false
        },
        {
          feature: 'Complete audit trail with version history for compliance',
          ours: true,
          miro: false,
          lucidChart: false,
          visio: false
        }
      ]
    }
  },
  methods: {
    openCheckout (planValue) {
      const plan = PLANS.find(p => p.value === planValue)

      // Anything uncertain — a subscription still loading, a user not resolved yet — falls through
      // to the checkout page rather than guessing at trial eligibility.
      const canSkipCheckout = SKIP_TRIAL_CHECKOUT &&
        !this.loadingActiveSubscription &&
        !!this.currentUser &&
        canStartCardlessTrial({
          plan,
          activeSubscription: this.activeSubscription,
          currentUser: this.currentUser
        })

      store.commit('payments/setPlan', plan)

      if (canSkipCheckout) {
        return this.startTrial()
      }

      this.$router.push('checkout')
    },
    openContactUs () {
      window.open('https://2gpm9s.share-eu1.hsforms.com/2Fdo_b7-hSES99T__J3YREA')
    },
    close () {
      const from = this.$route.query.from
      const back = window.history.state?.back

      if (isAppPath(from)) {
        this.$router.replace(from)
      } else if (isAppPath(back) && !isPlanChoicePath(back)) {
        this.$router.back()
      } else {
        this.$router.replace('/dashboard')
      }
    },
    shouldOpenMyPlan (activeSubscription) {
      const status = activeSubscription?.subscriptionDetails?.status

      return status === 'active' || (status === 'trialing' && !this.isSubscriptionAdmin)
    }
  },
  mounted () {
    if (this.subscriptionType) {
      this.localSubscriptionType = this.subscriptionType
    }
    if (this.shouldOpenMyPlan(this.activeSubscription)) {
      this.$router.push('/subscription/my-plan')
    }
  },
  watch: {
    localSubscriptionType: function (subscriptionType) {
      store.commit('payments/setSubscriptionType', subscriptionType)
    },
    activeSubscription: function (activeSubscription) {
      // A trial started from this page refreshes the subscription on its way to the dashboard;
      // without this guard that refresh would redirect to My Plan mid-flight.
      if (this.loadingStartTrial) {
        return
      }
      if (this.shouldOpenMyPlan(activeSubscription)) {
        this.$router.push('/subscription/my-plan')
      }
    }
  }
}
</script>

<style scoped>
.content {
  position: relative;
}

.closeButton {
  position: absolute;
  top: 16px;
  right: 24px;
}

.header {
  margin-bottom: 56px;
}

.pageTitle {
  text-align: center;
  color: var(--text-dark);
  margin-bottom: 16px;
  font-family: Faktum, sans-serif;
  font-size: 52px;
  font-weight: 400;
  line-height: 62.4px;
  letter-spacing: -0.05em;
}

.pageDescription {
  font-size: 18px;
  font-weight: 400;
  line-height: 28.8px;
  text-align: center;
  letter-spacing: -0.02em;
  color: rgba(0, 0, 0, 0.80);
  max-width: 421px;
  margin: 0 auto;
}

[data-theme="dark"] .pageDescription {
  color: var(--text-dark);
}

.vatNote {
  text-align: center;
  font-size: 14px;
  margin-bottom: 64px;
  color: rgba(var(--v-theme-on-surface), 0.6);
}

.plans {
  gap: 30px;
  display: flex;
  flex-wrap: wrap;
  padding: 0 20px;
  margin-bottom: 24px;
  align-items: stretch;
  justify-content: center;
}

.plans :deep(.planCard) {
  flex-basis: 240px;
  flex-shrink: 1;
  min-width: 240px;
  display: flex;
  flex-direction: column;
}

.plans :deep(.planCardInner) {
  flex: 1;
}

.plans :deep(.price) {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

@media (max-width: 1000px) {
  .plans :deep(.planCard) {
    flex-basis: calc(50% - 15px);
    min-width: calc(50% - 15px);
  }
}

@media (max-width: 700px) {
  .plans :deep(.planCard) {
    flex-basis: 100%;
    min-width: 100%;
  }
}

.compareInfo {
  width: 100%;
  margin: 0 auto;
  max-width: 1512px;
}

.compareInfoTitle {
  font-size: 36px;
  font-weight: 500;
  line-height: 42px;
  text-align: center;
  color: var(--dark-4);
  margin-bottom: 38px;
}

.compareInfoTable {
  width: 100%;
  margin: 0 auto 64px;
  border-collapse: collapse;

  tr {
    &:last-child {
      .compareInfoTableItem {
        border-bottom: none;
      }
    }
  }
}

.compareInfoTableHeader {
  font-weight: 400;
  font-size: 25px;
  padding: 14px 0;
  line-height: 32.5px;
  font-family: Faktum, sans-serif;
  border-bottom: 1px solid var(--light-4);
}

.compareInfoTableItem {
  font-size: 16px;
  color: var(--dark-4);
  line-height: 24px;
  padding: 8px 16px;
  border-bottom: 1px solid var(--light-4);

  .compareInfoTableItemInner {
    min-width: 100px;
    display: flex;
    justify-content: center;
  }

  &:first-child {
    max-width: 300px;

    .compareInfoTableItemInner {
      justify-content: flex-start;
    }
  }
}

.faq {
  gap: 16px;
  padding: 48px;
  background-color: var(--light-1);
}

.faqInner {
  max-width: 772px;
  margin: 0 auto;

  :global(.v-expansion-panels:not(.v-expansion-panels--variant-accordion) > :not(:first-child):not(:last-child):not(.v-expansion-panel--active):not(.v-expansion-panel--before-active)),
  :global(.v-expansion-panels:not(.v-expansion-panels--variant-accordion) > :not(:first-child):not(:last-child):not(.v-expansion-panel--active):not(.v-expansion-panel--after-active)),
  :global(.v-expansion-panels:not(.v-expansion-panels--variant-accordion) > :first-child:not(:last-child):not(.v-expansion-panel--active):not(.v-expansion-panel--before-active)),
  :global(.v-expansion-panels:not(.v-expansion-panels--variant-accordion) > :last-child:not(:first-child):not(.v-expansion-panel--active):not(.v-expansion-panel--after-active)) {
    border-radius: 8px !important;
  }

  :global(.v-expansion-panel-title) {
    font-size: 20px;
    font-weight: 500;
    font-family: Faktum, sans-serif;
    color: var(--text-dark, #1F2833);
  }

  :global(.v-expansion-panel-text__wrapper) {
    font-size: 16px;
    font-weight: 400;
    line-height: 25.6px;
    color: var(--text-dark, #1F2833);
  }

  :global(.v-expansion-panel::after) {
    display: none;
  }

  :global(.v-expansion-panel) {
    border-radius: 8px;
    margin-bottom: 8px;
    border: 1px solid var(--light-6);
  }

  :global(.v-expansion-panel__shadow) {
    box-shadow: none;
  }
}
</style>
